import { DataSourceRequestState, toDataSourceRequestString } from "@progress/kendo-data-query";
import { Button, ButtonGroup, Toolbar } from "@progress/kendo-react-buttons";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn as Column, GridDataStateChangeEvent, GridNoRecords, GridRowClickEvent } from "@progress/kendo-react-grid";
import { arrowRotateCwIcon, filterClearIcon } from "@progress/kendo-svg-icons";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { Link, useHistory, useParams } from "react-router-dom";
import { Title } from "../../utils/title";
import { fetchApi } from "../../services/api";
import { GridLoadingPanel } from "../../components/GridLoadingPanel";
import SimpleDateRangePicker from "../../components/SimpleDateRangePicker";
import DriverAutoComplete from "./DriverAutoComplete";
import DateCell from "../../components/cells/DateCell";
import ScoreDisputeDetails from "./ScoreDisputeDetails";
import { useLocalStorage } from "usehooks-ts";
import { IDNameViewModel } from "TypeGen/id-name-view-model";

type EventTypeViewModel = {
  ID: number;
  Name: string;
  GroupName: string;
};

type DisputeResponse = {
  Data: DisputeSummaryViewModel[];
  Statuses: IDNameViewModel[];
  EventTypes: EventTypeViewModel[];
  Total: number;
};

type DisputeSummaryViewModel = {
  DriverScoreEventID: number;
  DriverID: number;
  DriverName: string;
  DriverNumber: string;
  EventType: number;
  EventTypeLabel: string;
  ScoreChange: number;
  IsUndone: boolean;
  DisputeStatus: number | null;
  DisputeStatusLabel: string;
  DisputeSubmittedOn: string;
  DisputeResolvedOn: string | null;
  DisputeHandledByUserID: string;
  DisputeHandledByFullName: string;
}

const DriverScoreDisputes = () => {
  const history = useHistory();
  const { driverScoreEventId } = useParams<{ driverScoreEventId?: string }>();
  const rawRouteId = driverScoreEventId ? Number(driverScoreEventId) : null;
  const routeSelectedId = rawRouteId && !Number.isNaN(rawRouteId) ? rawRouteId : null;

  const [gridState, setGridState] = useState<DataSourceRequestState>({
    skip: 0,
    take: 25,
    sort: [{ field: "DisputeSubmittedOn", dir: "desc" }]
  });
  const [dateRange, setDateRange] = useState<SelectionRange>({
    start: dayjs().utc().subtract(30, "day").toDate(),
    end: dayjs().utc().toDate()
  });
  const [statusFilter, setStatusFilter] = useLocalStorage<number[]>("DriverScoreDisputes_StatusFilter_2", [100]);
  const [eventTypeFilter, setEventTypeFilter] = useLocalStorage<number[]>("DriverScoreDisputes_EventTypeFilter_2", []);
  const [driverId, setDriverId] = useState<number | null>(null);

  const [statuses, setStatuses] = useState<IDNameViewModel[]>([]);
  const [eventTypes, setEventTypes] = useState<EventTypeViewModel[]>([]);
  const [disputes, setDisputes] = useState<DisputeSummaryViewModel[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(routeSelectedId);
  const selectedIdRef = useRef<number | null>(routeSelectedId);
  const routeSelectedIdRef = useRef<number | null>(routeSelectedId);

  useEffect(() => {
    setSelectedId(routeSelectedId);
  }, [routeSelectedId]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    routeSelectedIdRef.current = routeSelectedId;
  }, [routeSelectedId]);

  const fetchDisputes = useCallback(() => {
    setLoading(true);
    const queryStr = toDataSourceRequestString(gridState);
    const payload = {
      From: dateRange.start ? dayjs(dateRange.start).utc().startOf("day").toISOString() : null,
      To: dateRange.end ? dayjs(dateRange.end).utc().endOf("day").toISOString() : null,
      StatusIDs: statusFilter,
      EventTypeIDs: eventTypeFilter,
      DriverID: driverId
    };

    fetchApi(`/api/Asset/DriverScoreEventDisputes?${queryStr}`, payload, "POST")
      .then((response: DisputeResponse) => {
        setDisputes(response.Data);
        setTotal(response.Total);
        setStatuses(response.Statuses);
        setEventTypes(response.EventTypes);

        if (response.Data?.length) {
          const routeSelection = routeSelectedIdRef.current;
          const currentSelection = selectedIdRef.current ?? routeSelection;
          const exists = currentSelection ? response.Data.some(x => x.DriverScoreEventID === currentSelection) : false;

          if (!currentSelection) {
            setSelectedId(response.Data[0].DriverScoreEventID);
          } else if (!exists && !routeSelection) {
            setSelectedId(response.Data[0].DriverScoreEventID);
          }
        } else {
          setSelectedId(null);
        }
      })
      .catch(() => alert("Unable to load disputes"))
      .finally(() => setLoading(false));
  }, [gridState, dateRange, statusFilter, eventTypeFilter, driverId]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setGridState(e.dataState);
  };

  const onRowClick = (e: GridRowClickEvent) => {
    // Keep selection instant; URL update should not trigger a refetch.
    setSelectedId(e.dataItem.DriverScoreEventID);
    history.push(`/Assets/DriverScoreDisputes/${e.dataItem.DriverScoreEventID}`);
  };

  const clearFilters = () => {
    setDateRange({ start: dayjs().utc().subtract(30, "day").toDate(), end: dayjs().utc().toDate() });
    setStatusFilter([100]);
    setEventTypeFilter([]);
    setDriverId(null);
    setGridState({ ...gridState, skip: 0 });
  };

  const gridData = disputes.map(item => ({
    ...item,
    selected: item.DriverScoreEventID === selectedId
  }));

  return <>
    <Title string="Driver Score Disputes" />
    <Toolbar className="my-3">
      <Button svgIcon={filterClearIcon} title="Clear Filters and Sort" onClick={clearFilters} />
      <Button svgIcon={arrowRotateCwIcon} title="Refresh" onClick={fetchDisputes} />
      <SimpleDateRangePicker value={dateRange} onChange={setDateRange} />
      <ButtonGroup>
        {statuses.map((status) => <Button
          key={status.ID}
          togglable={true}
          selected={statusFilter.includes(status.ID)}
          onClick={() => {
            if (statusFilter.includes(status.ID)) {
              setStatusFilter(statusFilter.filter((id) => id !== status.ID));
            } else {
              setStatusFilter([...statusFilter, status.ID]);
            }
          }}
        >
          {status.Name}
        </Button>)}
      </ButtonGroup>
      <MultiSelect
        data={eventTypes}
        dataItemKey="ID"
        textField="Name"
        groupField="GroupName"
        placeholder="Event Types"
        style={{ width: 360 }}
        value={eventTypes.filter((opt) => eventTypeFilter.includes(opt.ID))}
        onChange={(e) => {
          const options = e.value as EventTypeViewModel[];
          setEventTypeFilter(options.map((opt) => opt.ID));
          setGridState({ ...gridState, skip: 0 });
        }}
      />
      <DriverAutoComplete
        selectedDriverID={driverId ?? undefined}
        onSelectedDriver={(driver) => {
          setDriverId(driver?.DriverID ?? null);
          setGridState({ ...gridState, skip: 0 });
        }}
        placeholder="Select driver"
        style={{ width: 200 }}
      />
    </Toolbar>

    <div className="row">
      <div className="col-lg-7">
        {loading && <GridLoadingPanel />}
        <Grid
          scrollable="none"
          data={gridData}
          select={{ [selectedId]: true }}
          dataItemKey="DriverScoreEventID"
          total={total}
          pageable
          sortable
          selectable={{ mode: "single" }}
          {...gridState}
          onDataStateChange={onDataStateChange}
          onRowClick={onRowClick}
          rows={{ data: (rowProps) => <tr {...rowProps.trProps} style={ rowProps.dataItem.IsUndone ? { opacity: '0.5' } : {} }>{rowProps.children}</tr>}}
        >
          <GridNoRecords>
            No disputes found.
          </GridNoRecords>
          <Column field="DisputeSubmittedOn" title="Submitted" cells={{ data: DateCell }} />
          <Column field="DriverNumber" title="Driver #" cells={{ data: (e) => <td {...e.tdProps}>
            <Link style={{ color: "#007bff" }} to={`/Assets/Driver/${e.dataItem.DriverID}`}>{e.dataItem.DriverNumber}</Link>
          </td> }} />
          <Column field="EventTypeLabel" title="Event" />
          <Column field="DisputeStatusLabel" title="Status" />
          <Column field="DisputeHandledByFullName" title="Resolved By" />
          <Column field="DisputeResolvedOn" title="Resolved On" cells={{ data: DateCell }}  />
        </Grid>
      </div>

      <div className="col-lg-5">
        <ScoreDisputeDetails
          disputeId={selectedId}
          onUpdated={fetchDisputes}
        />
      </div>
    </div>
  </>
};

export default DriverScoreDisputes;
